import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import {
  Search,
  Plus,
  Building2,
  ChevronRight,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import { cn, formatDate } from '@/lib/utils';
import { AddProspectModal, type NewProspect } from '@/components/modals/AddProspectModal';
import {
  GET_PROSPECTS,
  CREATE_PROSPECT,
  type ProspectsQueryResult,
  type Prospect,
  type CreateProspectResult,
} from '@/graphql/prospects';

type ProspectStatus = 'PROSPECT' | 'ACTIVE' | 'INACTIVE';

const STATUS_BADGES: Record<ProspectStatus, { label: string; className: string }> = {
  PROSPECT: { label: 'Prospect', className: 'badge-primary' },
  ACTIVE: { label: 'Active', className: 'badge-secondary' },
  INACTIVE: { label: 'Inactive', className: 'badge-neutral' },
};

export function ProspectList() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'CFI' | 'PSP' | 'all'>('all');
  const [showAddModal, setShowAddModal] = useState(false);

  // GraphQL query for prospects
  const { data, loading, error, refetch } = useQuery<ProspectsQueryResult>(GET_PROSPECTS, {
    variables: {
      type: typeFilter !== 'all' ? typeFilter : undefined,
      search: searchQuery || undefined,
      limit: 100,
      offset: 0,
    },
    fetchPolicy: 'cache-and-network',
  });

  // GraphQL mutation for creating prospects
  const [createProspect, { loading: creating }] = useMutation<CreateProspectResult>(CREATE_PROSPECT, {
    onCompleted: () => {
      setShowAddModal(false);
      refetch();
    },
    onError: (err) => {
      console.error('Failed to create prospect:', err);
    },
  });

  const prospects = data?.prospects.items || [];
  const totalCount = data?.prospects.pageInfo.totalCount || 0;

  const handleRowClick = (prospectId: string) => {
    navigate(`/sales/prospects/${prospectId}`);
  };

  const handleAddProspect = (newProspect: NewProspect) => {
    createProspect({
      variables: {
        input: {
          name: newProspect.companyName,
          type: 'CFI', // Default to CFI, could be extended in the modal
          website: undefined,
          phone: newProspect.contactPhone || undefined,
        },
      },
    });
  };

  // Get primary contact info for display
  const getContactInfo = (prospect: Prospect) => {
    const contact = prospect.primaryContact || prospect.contacts[0];
    if (!contact) {
      return { name: 'No contact', email: '-' };
    }
    return {
      name: `${contact.firstName} ${contact.lastName}`,
      email: contact.email,
    };
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate dark:text-mist">
            Prospects
          </h1>
          <p className="text-chalkboard dark:text-rain-cloud mt-1">
            {loading ? 'Loading...' : `${totalCount} prospect${totalCount !== 1 ? 's' : ''} found`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => refetch()} 
            className="btn-secondary"
            disabled={loading}
          >
            <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          </button>
          <button 
            className="btn-primary" 
            onClick={() => setShowAddModal(true)}
            disabled={creating}
          >
            <Plus className="w-4 h-4" />
            Add Prospect
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="card bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <div className="card-body">
            <div className="flex items-center gap-3 text-red-700 dark:text-red-400">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <div>
                <p className="font-medium">Error loading prospects</p>
                <p className="text-sm opacity-80">{error.message}</p>
              </div>
              <button 
                onClick={() => refetch()} 
                className="ml-auto btn-secondary btn-sm"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', alignItems: 'center' }}>
            <div className="relative" style={{ flex: '1 1 auto', minWidth: 0 }}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-french-gray pointer-events-none" />
              <input
                type="text"
                placeholder="Search prospects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input"
                style={{ width: '100%', paddingLeft: '2.5rem' }}
              />
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as 'CFI' | 'PSP' | 'all')}
              className="select"
              style={{ flex: '0 0 auto', width: '12rem' }}
            >
              <option value="all">All Types</option>
              <option value="CFI">CFI (Bank/Credit Union)</option>
              <option value="PSP">PSP (Service Provider)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Prospects Table */}
      <div className="card overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Type</th>
              <th>Contact</th>
              <th>Status</th>
              <th>Created</th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {loading && prospects.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto text-pond dark:text-pond-light" />
                  <p className="mt-2 text-chalkboard dark:text-rain-cloud">Loading prospects...</p>
                </td>
              </tr>
            ) : prospects.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8">
                  <p className="text-chalkboard dark:text-rain-cloud">
                    {searchQuery || typeFilter !== 'all' 
                      ? 'No prospects match your filters.' 
                      : 'No prospects yet. Click "Add Prospect" to create one.'}
                  </p>
                </td>
              </tr>
            ) : (
              prospects.map((prospect) => {
                const statusBadge = STATUS_BADGES[prospect.status] || STATUS_BADGES.PROSPECT;
                const contactInfo = getContactInfo(prospect);

                return (
                  <tr
                    key={prospect.id}
                    onClick={() => handleRowClick(prospect.id)}
                    className="cursor-pointer hover:bg-pale-gray-1 dark:hover:bg-navy/50"
                  >
                    <td>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-chalkboard dark:text-rain-cloud" />
                        <span className="font-medium text-slate dark:text-mist">
                          {prospect.name}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={cn(
                        'badge',
                        prospect.type === 'CFI' ? 'badge-primary' : 'badge-warning'
                      )}>
                        {prospect.type}
                      </span>
                    </td>
                    <td>
                      <div>
                        <p className="text-sm text-slate dark:text-mist">{contactInfo.name}</p>
                        <p className="text-xs text-chalkboard dark:text-rain-cloud">
                          {contactInfo.email}
                        </p>
                      </div>
                    </td>
                    <td>
                      <span className={cn('badge', statusBadge.className)}>
                        {statusBadge.label}
                      </span>
                    </td>
                    <td className="text-sm text-chalkboard dark:text-rain-cloud">
                      {formatDate(new Date(prospect.createdAt))}
                    </td>
                    <td>
                      <ChevronRight className="w-4 h-4 text-chalkboard dark:text-rain-cloud" />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination Info */}
        {data?.prospects.pageInfo && (
          <div className="px-6 py-3 border-t border-gray dark:border-night-pond">
            <p className="text-sm text-chalkboard dark:text-rain-cloud">
              Showing {prospects.length} of {totalCount} prospects
            </p>
          </div>
        )}
      </div>

      {/* Add Prospect Modal */}
      <AddProspectModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddProspect}
      />
    </div>
  );
}
